import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Package, Image as ImageIcon, X, Minus } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Toast';
import { Modal } from '../components/Modal';
import { PageLoading } from '../components/Loading';
import { ColorCircle } from '../components/ColorCircle';
import type { Producto, CreateProductoRequest, UpdateProductoRequest } from '../types';
import styles from './Productos.module.css';

const CATALOG_URL = 'https://topstore-catalogo.businesspocho118.workers.dev';

// Función para normalizar URLs de imágenes
const normalizeImageUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${CATALOG_URL}/${url}`;
};

// Función auxiliar para obtener el nombre del color sin el HEX
const getColorName = (color: string): string => {
  if (color.includes(' #')) return color.split(' #')[0];
  if (color.includes('#')) return color.split('#')[0];
  return color;
};

interface FormData extends CreateProductoRequest {
  unidades: Record<string, number>;
}

const initialForm: FormData = {
  product_id: '',
  nombre: '',
  descripcion: '',
  precio: '',
  colores: '',
  tallas: '',
  unidades: {},
  stock: 0,
  genero: 'mujeres',
  categoria: '',
  image_paths: []
};

export function Productos() {
  const { getProductos, createProducto, updateProducto, deleteProducto, isLoading, checkConnection, loadFromSupabaseAndSave, isSupabaseConnected } = useApi();
  const { showToast } = useToast();
  
  const [productos, setProductos] = useState<Producto[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<Producto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenero, setFilterGenero] = useState<'hombres' | 'mujeres'>('mujeres');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProducto, setEditingProducto] = useState<Producto | null>(null);
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [imageInput, setImageInput] = useState('');

  // Stats computados dinámicamente según el filtro de género
  const stats = useMemo(() => {
    const activos = productos.filter(p => p.activo);
    let filtered = activos.filter(p => p.genero === filterGenero);
    
    const total = filtered.length;
    const sinStock = filtered.filter(p => p.stock === 0).length;
    const conStock = total - sinStock;
    
    return { total, sinStock, conStock };
  }, [productos, filterGenero]);

  useEffect(() => {
    loadProductos();
    
    // Recargar cuando la ventana recibe foco
    const handleFocus = async () => {
      console.log('Ventana recibieron foco - recargando productos desde Supabase...');
      await checkConnection();
      if (isSupabaseConnected()) {
        await loadFromSupabaseAndSave();
      }
      loadProductos();
    };
    
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('Página visible - recargando productos desde Supabase...');
        await checkConnection();
        if (isSupabaseConnected()) {
          await loadFromSupabaseAndSave();
        }
        loadProductos();
      }
    };
    
    // Recargar cada 30 segundos para mantener sincronizado entre dispositivos
    const syncInterval = setInterval(async () => {
      console.log('[Auto-sync] Recargando productos cada 30 segundos...');
      await checkConnection();
      if (isSupabaseConnected()) {
        await loadFromSupabaseAndSave();
      }
      loadProductos();
    }, 30000);
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(syncInterval);
    };
  }, []);

  const loadProductos = async () => {
    const result = await getProductos();
    if (result.success && result.data) {
      setProductos(result.data);
      setFilteredProductos(result.data);
    }
  };

  useEffect(() => {
    let filtered = productos;
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.product_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.categoria.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    filtered = filtered.filter(p => p.genero === filterGenero);
    
    setFilteredProductos(filtered);
  }, [searchTerm, filterGenero, productos]);

  const handleOpenModal = (producto?: Producto) => {
    if (producto) {
      setEditingProducto(producto);
      // Usar el campo unidades existente o inicializar vacío
      const unidades = producto.unidades || {};
      
      setFormData({
        product_id: producto.product_id,
        nombre: producto.nombre,
        descripcion: producto.descripcion,
        precio: producto.precio,
        colores: producto.colores,
        tallas: producto.tallas || '',
        unidades: unidades,
        stock: producto.stock || 0,
        genero: producto.genero,
        categoria: producto.categoria,
        image_paths: producto.image_paths
      });
    } else {
      setEditingProducto(null);
      setFormData(initialForm);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProducto(null);
    setFormData(initialForm);
    setImageInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Asegurar que activo esté presente
    const productoData = { ...formData, activo: true };
    
    if (editingProducto) {
      try {
        await updateProducto(editingProducto.id, productoData as UpdateProductoRequest);
        showToast('Producto actualizado correctamente', 'success');
        handleCloseModal();
      } catch (err) {
        showToast('Error al actualizar', 'error');
      }
    } else {
      try {
        await createProducto(productoData as any);
        showToast('Producto creado correctamente', 'success');
        handleCloseModal();
      } catch (err) {
        showToast('Error al crear', 'error');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await deleteProducto(id);
      showToast('Producto eliminado', 'success');
    } catch (err) {
      showToast('Error al eliminar', 'error');
    }
  };

  const addImage = () => {
    if (imageInput.trim() && !formData.image_paths.includes(imageInput.trim())) {
      setFormData(prev => ({
        ...prev,
        image_paths: [...prev.image_paths, imageInput.trim()]
      }));
      setImageInput('');
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      image_paths: prev.image_paths.filter((_, i) => i !== index)
    }));
  };

  // Actualizar stock de una combinación específica color+talla
  const updateUnidadStock = (color: string, talla: string, delta: number) => {
    const key = `${color}-${talla}`;
    setFormData(prev => {
      const newUnidades = { ...(prev.unidades || {}) };
      const currentStock = newUnidades[key] || 0;
      const newStock = Math.max(0, currentStock + delta);
      newUnidades[key] = newStock;
      
      // Calcular stock total
      const totalStock = Object.values(newUnidades).reduce((a, b) => a + b, 0);
      
      return {
        ...prev,
        unidades: newUnidades,
        stock: totalStock
      };
    });
  };

  // Cuando cambian los colores, limpiar unidades que ya no existen
  const handleColorsChange = (newColors: string) => {
    setFormData(prev => {
      const colorList = newColors.split(', ').map(c => c.trim()).filter(c => c);
      const tallaList = (prev.tallas || '').split(', ').filter(t => t.trim());
      const newUnidades: Record<string, number> = {};
      
      // Mantener solo las combinaciones que siguen siendo válidas
      colorList.forEach(color => {
        tallaList.forEach(talla => {
          const key = `${color}-${talla}`;
          if (prev.unidades && prev.unidades[key] !== undefined) {
            newUnidades[key] = prev.unidades[key];
          }
        });
      });
      
      const totalStock = Object.values(newUnidades).reduce((a, b) => a + b, 0);
      
      return {
        ...prev,
        colores: newColors,
        unidades: newUnidades,
        stock: totalStock
      };
    });
  };

  // Cuando cambian las tallas, limpiar unidades que ya no existen
  const handleTallasChange = (newTallas: string) => {
    setFormData(prev => {
      const colorList = (prev.colores || '').split(', ').filter((c: string) => c.trim());
      const tallaList = newTallas.split(', ').filter((t: string) => t.trim());
      const newUnidades: Record<string, number> = {};
      
      // Mantener solo las combinaciones que siguen siendo válidas
      colorList.forEach((color: string) => {
        tallaList.forEach((talla: string) => {
          const key = `${color}-${talla}`;
          if (prev.unidades && prev.unidades[key] !== undefined) {
            newUnidades[key] = prev.unidades[key];
          }
        });
      });
      
      const totalStock = Object.values(newUnidades).reduce((a, b) => a + b, 0);
      
      return {
        ...prev,
        tallas: newTallas,
        unidades: newUnidades,
        stock: totalStock
      };
    });
  };

  const categorias = ['Camisas', 'Buzos', 'Chaquetas', 'Conjuntos', 'Enterizos', 'Leggings', 'Shorts', 'Tops', 'Medias'];

  if (isLoading && productos.length === 0) {
    return <PageLoading />;
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Inventario</h1>
          <p className={styles.subtitle}>
            {stats.total} productos ({stats.conStock} con stock • {stats.sinStock} sin stock)
          </p>
        </div>
      </header>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterButtons}>
          <button
            className={`${styles.filterBtn} ${filterGenero === 'mujeres' ? styles.active : ''}`}
            onClick={() => setFilterGenero('mujeres')}
          >
            Mujeres
          </button>
          <button
            className={`${styles.filterBtn} ${filterGenero === 'hombres' ? styles.active : ''}`}
            onClick={() => setFilterGenero('hombres')}
          >
            Hombres
          </button>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProductos.length === 0 ? (
        <div className={styles.empty}>
          <Package size={48} />
          <h3>No se encontraron productos</h3>
          <p>Intenta con otros filtros de búsqueda</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {filteredProductos.map((producto) => (
            <div key={producto.id} className={styles.card}>
              <div className={styles.cardImage}>
                {producto.image_paths[0] ? (
                  <img src={normalizeImageUrl(producto.image_paths[0])} alt={producto.nombre} />
                ) : (
                  <div className={styles.noImage}>
                    <ImageIcon size={32} />
                  </div>
                )}
                <div className={styles.cardOverlay}>
                  <button 
                    className={styles.editBtn}
                    onClick={() => handleOpenModal(producto)}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(producto.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <span className={`${styles.badge} ${producto.stock > 0 ? styles.inStock : styles.outOfStock}`}>
                  {producto.stock > 0 ? `${producto.stock} uds` : 'Sin stock'}
                </span>
              </div>
              <div className={styles.cardContent}>
                <span className={styles.categoria}>{producto.categoria}</span>
                <h3 className={styles.productName}>{producto.nombre}</h3>
                <p className={styles.precio}>{producto.precio}</p>
                
                {/* Mostrar colores como círculos y tallas como texto */}
                {(producto.colores || producto.tallas) && (
                  <div className={styles.colorCircles}>
                    {/* Círculos de colores disponibles */}
                    {producto.colores && producto.colores.split(', ').filter(c => c.trim()).map((color, idx) => (
                      <ColorCircle key={color} color={color} size="sm" />
                    ))}
                    {/* Mostrar talla si hay una sola, o "X tallas" si hay varias */}
                    {producto.tallas && producto.tallas.split(', ').filter(t => t.trim()).length === 1 && (
                      <span className={styles.tallaLabel}>{producto.tallas}</span>
                    )}
                    {producto.tallas && producto.tallas.split(', ').filter(t => t.trim()).length > 1 && (
                      <span className={styles.tallaLabel}>{producto.tallas.split(', ').filter(t => t.trim()).join(', ')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProducto ? 'Editar Producto' : 'Nuevo Producto'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {editingProducto ? 'Guardar Cambios' : 'Crear Producto'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>ID del Producto</label>
              <input
                type="text"
                value={formData.product_id}
                onChange={(e) => setFormData(prev => ({ ...prev, product_id: e.target.value }))}
                className="input"
                placeholder="camisa-workout"
                required
                disabled={!!editingProducto}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Nombre</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                className="input"
                placeholder="Camisa WORKOUT"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Precio</label>
              <input
                type="text"
                value={formData.precio}
                onChange={(e) => setFormData(prev => ({ ...prev, precio: e.target.value }))}
                className="input"
                placeholder="$35.000"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Stock Total (calculado)</label>
              <div className={styles.stockDisplay}>
                <span className={styles.stockTotal}>{formData.stock}</span>
                <span className={styles.stockLabel}>unidades totales</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Género</label>
              <select
                value={formData.genero}
                onChange={(e) => setFormData(prev => ({ ...prev, genero: e.target.value as 'hombres' | 'mujeres' }))}
                className="input"
              >
                <option value="mujeres">Mujeres</option>
                <option value="hombres">Hombres</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Categoría</label>
              <select
                value={formData.categoria}
                onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                className="input"
                required
              >
                <option value="">Seleccionar...</option>
                {categorias.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Colores</label>
              <div className={styles.coloresTags}>
                {(formData.colores || '').split(', ').filter((c: string) => c.trim()).map((color: string) => (
                  <span key={color} className={styles.colorTag}>
                    <ColorCircle color={color} size="sm" />
                    {getColorName(color)}
                    <button 
                      type="button" 
                      onClick={() => {
                        const newColors = (formData.colores || '').split(', ').filter((c: string) => c.trim()).filter(c => c !== color);
                        // También borrar las unidades de ese color
                        const newUnidades = { ...(formData.unidades || {}) };
                        Object.keys(newUnidades).forEach(key => {
                          if (key.startsWith(color + '-') || key.startsWith(color + ' #') || key.startsWith(color + '#')) {
                            delete newUnidades[key];
                          }
                        });
                        // Recalcular stock total
                        const totalStock = Object.values(newUnidades).reduce((a: number, b: number) => a + b, 0);
                        setFormData({
                          ...formData,
                          colores: newColors.join(', '),
                          unidades: newUnidades,
                          stock: totalStock
                        });
                      }}
                      className={styles.removeColor}
                    >×</button>
                  </span>
                ))}
                {/* Color picker + nombre personalizado */}
                <div className={styles.colorPickerContainer}>
                  <input
                    type="text"
                    placeholder="Nombre (ej: Azul)"
                    className={styles.colorNameInput}
                    id="colorNameInput"
                  />
                  <input
                    type="color"
                    id="colorPickerInput"
                    className={styles.colorPicker}
                    title="Seleccionar color"
                    defaultValue="#000000"
                  />
                  <button 
                    type="button"
                    className={styles.addColorBtn}
                    onClick={() => {
                      const nameInput = document.getElementById('colorNameInput') as HTMLInputElement;
                      const picker = document.getElementById('colorPickerInput') as HTMLInputElement;
                      const colorName = nameInput?.value.trim();
                      const colorValue = picker?.value;
                      // Guardar como "nombre HEX" (sin hashtag, con espacio)
                      const colorKey = colorName ? `${colorName} ${colorValue}` : colorValue;
                      if (colorKey) {
                        const currentColors = (formData.colores || '').split(', ').filter((c: string) => c.trim());
                        if (!currentColors.includes(colorKey)) {
                          setFormData({
                            ...formData,
                            colores: [...currentColors, colorKey].join(', ')
                          });
                          nameInput.value = '';
                        }
                      }
                    }}
                  >
                    + Añadir
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Editor de unidades por COLOR+TALLA */}
          {formData.colores && formData.tallas && (
            <div className={styles.formGroup}>
              <label>Inventario por Color y Talla</label>
              <div className={styles.unidadesEditor}>
                {/* Header */}
                <div className={styles.unidadesHeader}>
                  <span>Color</span>
                  <span>Talla</span>
                  <span>Cantidad</span>
                </div>
                {/* Grid de todas las combinaciones */}
                {formData.colores.split(', ').filter(c => c.trim()).map(color => 
                  formData.tallas.split(', ').filter(t => t.trim()).map(talla => {
                    const key = `${color}-${talla}`;
                    const cantidad = formData.unidades?.[key] || 0;
                    return (
                      <div key={key} className={styles.unidadRow}>
                        <div className={styles.unidadColor}>
                          <ColorCircle color={color} size="sm" />
                          <span>{getColorName(color)}</span>
                        </div>
                        <span className={styles.unidadTalla}>{talla}</span>
                        <div className={styles.stockControls}>
                          <button 
                            type="button"
                            className={styles.stockBtn}
                            onClick={() => updateUnidadStock(color, talla, -1)}
                          >
                            <Minus size={14} />
                          </button>
                          <span className={styles.stockValue}>{cantidad}</span>
                          <button 
                            type="button"
                            className={styles.stockBtn}
                            onClick={() => updateUnidadStock(color, talla, 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '8px' }}>
                Stock total: {Object.values(formData.unidades || {}).reduce((a: number, b: number) => a + b, 0)} unidades
              </p>
            </div>
          )}

          <div className={styles.formGroup}>
              <label>Tallas</label>
              <div className={styles.coloresTags}>
                {(formData.tallas || '').split(', ').filter((t: string) => t.trim()).map((talla: string) => (
                  <span key={talla} className={styles.colorTag}>
                    {talla}
                    <button 
                      type="button" 
                      onClick={() => {
                        const newTallas = (formData.tallas || '').split(', ').filter((t: string) => t.trim()).filter(t => t !== talla);
                        setFormData({...formData, tallas: newTallas.join(', ')});
                      }}
                      className={styles.removeColor}
                    >×</button>
                  </span>
                ))}
                <input
                  type="text"
                  list="tallasPredefinidas"
                  placeholder="+ Talla"
                  className={styles.colorInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                      const newTalla = e.currentTarget.value.trim().toUpperCase();
                      const currentTallas = (formData.tallas || '').split(', ').filter((t: string) => t.trim());
                      if (!currentTallas.includes(newTalla)) {
                        setFormData({
                          ...formData,
                          tallas: [...currentTallas, newTalla].join(', ')
                        });
                      }
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <datalist id="tallasPredefinidas">
                  <option value="XS" />
                  <option value="S" />
                  <option value="M" />
                  <option value="L" />
                  <option value="XL" />
                  <option value="XXL" />
                  <option value="UNICA" />
                </datalist>
              </div>
            </div>

          {/* Editor de unidades por COLOR+TALLA */}
          {formData.colores && formData.tallas && (
            <div className={styles.formGroup}>
              <label>Inventario por Color y Talla</label>
              <div className={styles.unidadesEditor}>
                {/* Header */}
                <div className={styles.unidadesHeader}>
                  <span>Color</span>
                  <span>Talla</span>
                  <span>Cantidad</span>
                </div>
                {/* Grid de todas las combinaciones */}
                {formData.colores.split(', ').filter(c => c.trim()).map(color => 
                  formData.tallas.split(', ').filter(t => t.trim()).map(talla => {
                    const key = `${color}-${talla}`;
                    const cantidad = formData.unidades?.[key] || 0;
                    return (
                      <div key={key} className={styles.unidadRow}>
                        <div className={styles.unidadColor}>
                          <ColorCircle color={color} size="sm" />
                          <span>{getColorName(color)}</span>
                        </div>
                        <span className={styles.unidadTalla}>{talla}</span>
                        <div className={styles.stockControls}>
                          <button 
                            type="button"
                            className={styles.stockBtn}
                            onClick={() => updateUnidadStock(color, talla, -1)}
                          >
                            <Minus size={14} />
                          </button>
                          <span className={styles.stockValue}>{cantidad}</span>
                          <button 
                            type="button"
                            className={styles.stockBtn}
                            onClick={() => updateUnidadStock(color, talla, 1)}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '8px' }}>
                Stock total: {Object.values(formData.unidades || {}).reduce((a: number, b: number) => a + b, 0)} unidades
              </p>
            </div>
          )}

          <div className={styles.formGroup}>
            <label>Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              className="input"
              rows={3}
              placeholder="Descripción del producto..."
            />
          </div>

          <div className={styles.formGroup}>
            <label>Imágenes (URLs)</label>
            <div className={styles.imageInput}>
              <input
                type="text"
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                className="input"
                placeholder="URL de la imagen..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
              />
              <button type="button" className="btn btn-secondary" onClick={addImage}>
                <Plus size={16} />
              </button>
            </div>
            <div className={styles.imageList}>
              {formData.image_paths.map((url, index) => (
                <div key={index} className={styles.imageItem}>
                  <img src={url} alt={`Imagen ${index + 1}`} />
                  <button type="button" onClick={() => removeImage(index)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
